import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../api'; // Configura l'oggetto Axios
import { useEventTracking } from '../hooks/useAnalytics';
import { CategorySkeleton } from '../components/skeleton';

const AllCategoriesPage = () => {
  const { trackEvent } = useEventTracking();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await API.get('/categories/root');
        setCategories(response.data);
      } catch (error) {
        console.error('Errore nel recupero delle categorie:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []); // Rimossa la dipendenza trackEvent

  // Traccia la visualizzazione della pagina categorie quando i dati sono caricati
  useEffect(() => {
    if (!loading && categories.length > 0) {
      trackEvent('all_categories_view', {});
    }
  }, [loading, categories.length, trackEvent]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  if (loading) {
    return <CategorySkeleton showSidebar={false} />;
  }

  return (
    <div className="main-content-container categories-page">
      <div className="categories-header">
        <h1 className="categories-title">Esplora le Categorie</h1>
        <p className="categories-subtitle">Scopri tutti i prodotti organizzati per categoria</p>
      </div>
      
      <div className="categories-grid">
        {categories.map((category) => {
          const isExpanded = expandedCategories.has(category.id);
          const hasSubcategories = category.subcategories.length > 0;
          
          return (
            <div key={category.id} className="category-card">
              <div className="category-card-content">
                <div className="category-info">
                  <h3 className="category-title">
                    <Link 
                      to={`/categories/${category.category_handle}`}
                      onClick={() => trackEvent('category_view', category)}
                    >
                      {category.category_title}
                    </Link>
                  </h3>
                  
                  <div className="category-subcategories">
                    {hasSubcategories ? (
                      <div className="subcategories-list">
                        {isExpanded ? (
                          // Mostra tutte le sottocategorie quando espansa
                          category.subcategories.map((sub) => (
                            <Link 
                              key={sub.id} 
                              to={`/categories/${sub.category_handle}`} 
                              className="subcategory-link"
                              onClick={() => trackEvent('category_view', sub)}
                            >
                              {sub.category_title}
                            </Link>
                          ))
                        ) : (
                          // Mostra solo le prime 3 quando compressa
                          <>
                            {category.subcategories.slice(0, 3).map((sub) => (
                              <Link 
                                key={sub.id} 
                                to={`/categories/${sub.category_handle}`} 
                                className="subcategory-link"
                                onClick={() => trackEvent('category_view', sub)}
                              >
                                {sub.category_title}
                              </Link>
                            ))}
                            {category.subcategories.length > 3 && (
                              <span className="subcategories-more">
                                +{category.subcategories.length - 3} altre
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="no-subcategories">Nessuna sottocategoria</span>
                    )}
                  </div>
                </div>
                
                {hasSubcategories && (
                  <button 
                    className={`category-expand-btn ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleCategory(category.id)}
                    aria-label={isExpanded ? 'Comprimi sottocategorie' : 'Espandi sottocategorie'}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M8 4l8 8-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AllCategoriesPage;
